import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FileCopy as DuplicateIcon,
  Publish as PublishIcon,
  UnpublishedOutlined as UnpublishIcon
} from '@mui/icons-material';
import { FormDefinition, CreateFormRequest } from '../types/forms';
import { FormService } from '../services/formService';
import FormBuilder from '../components/forms/FormBuilder';
import FormPreview from '../components/forms/FormPreview';

const FormBuilderPage: React.FC = () => {
  const [forms, setForms] = useState<FormDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState<FormDefinition | null>(null);
  const [mode, setMode] = useState<'list' | 'create' | 'edit' | 'preview'>('list');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<FormDefinition | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      const formsData = await FormService.getForms();
      setForms(formsData);
    } catch (error) {
      console.error('Error loading forms:', error);
      showSnackbar('Error loading forms', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateForm = async (formData: CreateFormRequest) => {
    try {
      await FormService.createForm(formData);
      showSnackbar('Form created successfully', 'success');
      loadForms();
      setMode('list');
    } catch (error) {
      console.error('Error creating form:', error);
      showSnackbar('Error creating form', 'error');
    }
  };

  const handleUpdateForm = async (id: string, formData: Partial<CreateFormRequest>) => {
    try {
      await FormService.updateForm(id, formData);
      showSnackbar('Form updated successfully', 'success');
      loadForms();
      setMode('list');
    } catch (error) {
      console.error('Error updating form:', error);
      showSnackbar('Error updating form', 'error');
    }
  };

  const handleDeleteForm = async () => {
    if (!formToDelete) return;
    
    try {
      await FormService.deleteForm(formToDelete.id);
      showSnackbar('Form deleted successfully', 'success');
      loadForms();
      setDeleteDialogOpen(false);
      setFormToDelete(null);
    } catch (error) {
      console.error('Error deleting form:', error);
      showSnackbar('Error deleting form', 'error');
    }
  };

  const handleDuplicateForm = async (form: FormDefinition) => {
    try {
      await FormService.duplicateForm(form.id, `${form.name} (Copy)`);
      showSnackbar('Form duplicated successfully', 'success');
      loadForms();
    } catch (error) {
      console.error('Error duplicating form:', error);
      showSnackbar('Error duplicating form', 'error');
    }
  };

  const handleTogglePublish = async (form: FormDefinition) => {
    try {
      if (form.isActive) {
        await FormService.unpublishForm(form.id);
        showSnackbar('Form unpublished', 'success');
      } else {
        await FormService.publishForm(form.id);
        showSnackbar('Form published', 'success');
      }
      loadForms();
    } catch (error) {
      console.error('Error toggling form status:', error);
      showSnackbar('Error updating form status', 'error');
    }
  };

  const renderFormsList = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Forms (Data Schemas)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedForm(null);
            setMode('create');
          }}
          sx={{ borderRadius: 2 }}
        >
          Create Form
        </Button>
      </Box>
      <Typography>Forms are structured data with validation and a default visual input definition.</Typography>
      {loading ? (
        <Typography>Loading forms...</Typography>
      ) : (
        <Grid container spacing={2.5}>
          {forms.map((form) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={form.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {form.name}
                    </Typography>
                    <Chip 
                      label={form.isActive ? 'Published' : 'Draft'} 
                      color={form.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, height: '3em', overflow: 'hidden' }}>
                    {form.description || 'No description provided'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <Chip label={form.category} variant="outlined" size="small" />
                    <Chip label={`v${form.version}`} variant="outlined" size="small" />
                    <Chip label={`${form.fields.length} fields`} variant="outlined" size="small" />
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary">
                    Updated {new Date(form.updatedAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
                
                <Box sx={{ p: 1.5, pt: 0, display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Preview">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedForm(form);
                        setMode('preview');
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Edit">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedForm(form);
                        setMode('edit');
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Duplicate">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDuplicateForm(form)}
                    >
                      <DuplicateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title={form.isActive ? 'Unpublish' : 'Publish'}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleTogglePublish(form)}
                    >
                      {form.isActive ? <UnpublishIcon fontSize="small" /> : <PublishIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => {
                        setFormToDelete(form);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderFormBuilder = () => (
    <FormBuilder
      form={selectedForm}
      onSave={selectedForm ? 
        (formData) => handleUpdateForm(selectedForm.id, formData) : 
        handleCreateForm
      }
      onCancel={() => setMode('list')}
    />
  );

  const renderFormPreview = () => (
    selectedForm && (
      <FormPreview
        form={selectedForm}
        onBack={() => setMode('list')}
        onEdit={() => setMode('edit')}
      />
    )
  );

  return (
    <Box>
      {mode === 'list' && renderFormsList()}
      {(mode === 'create' || mode === 'edit') && renderFormBuilder()}
      {mode === 'preview' && renderFormPreview()}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{formToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteForm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormBuilderPage;